import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, fmtFecha } from '../utils/api';
import { Spinner, Empty, ConfirmModal, StatusBadge } from '../components/UI';
import NuevoClienteForm from '../components/NuevoClienteForm';

export default function Clientes() {
  const location = useLocation();
  const [clientes, setClientes]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [busqueda, setBusqueda]   = useState('');
  const [detalleId, setDetalleId] = useState(location.state?.idAbrir || null);
  const [creando, setCreando]     = useState(false);

  const cargar = () => {
    setLoading(true);
    api(`/clientes${busqueda.length >= 2 ? `?q=${encodeURIComponent(busqueda)}` : ''}`).then(res => {
      const vistos = new Set();
      setClientes(res.filter(r => { if (vistos.has(r.id)) return false; vistos.add(r.id); return true; }));
      setLoading(false);
    });
  };

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [busqueda]);

  if (creando) return (
    <div className="page-content">
      <h1 className="page-title">Nuevo cliente</h1>
      <NuevoClienteForm
        onCreado={() => { setCreando(false); cargar(); }}
        onCancelar={() => setCreando(false)}
      />
    </div>
  );

  if (detalleId) return (
    <DetalleCliente id={detalleId} onVolver={() => { setDetalleId(null); cargar(); }} />
  );

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Clientes</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>+ Nuevo cliente</button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input className="form-input" placeholder="Buscar por nombre o matrícula..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
      </div>

      {loading ? <Spinner texto="Cargando..." /> :
        clientes.length === 0 ? <Empty icon="👤" texto="Sin clientes" /> :
        <div className="tabla-wrap">
          <table className="tabla">
            <thead>
              <tr><th>ID</th><th>Nombre</th><th>Vehículo</th><th>Matrícula</th><th>Celular</th><th></th></tr>
            </thead>
            <tbody>
              {clientes.map(c => (
                <tr key={c.id}>
                  <td><span className="badge badge-gris">{c.id}</span></td>
                  <td style={{ fontWeight: 500 }}>{c.nombre}</td>
                  <td>{[c.marca, c.modelo].filter(Boolean).join(' ') || '—'}</td>
                  <td>{c.matricula || '—'}</td>
                  <td>{c.celular || '—'}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => setDetalleId(c.id)}>Ver</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function CampoEditable({ etiqueta, valor, onGuardar, tipo = 'text', placeholder = '', multilinea = false }) {
  const [editando, setEditando] = useState(false);
  const [temp, setTemp] = useState(valor || '');

  useEffect(() => setTemp(valor || ''), [valor]);

  const guardar = async () => {
    await onGuardar(temp);
    setEditando(false);
  };

  return (
    <div>
      <div className="sec-header" style={{ marginBottom: '.3rem' }}>
        <p style={{ fontWeight: 600, fontSize: '.8125rem', color: 'var(--gris-5)' }}>{etiqueta}</p>
        {!editando && (
          <button className="btn btn-outline btn-sm" onClick={() => setEditando(true)}>✏️ Editar</button>
        )}
      </div>
      {editando ? (
        <div style={{ display: 'flex', gap: '.5rem', alignItems: multilinea ? 'flex-start' : 'center', flexWrap: 'wrap' }}>
          {multilinea ? (
            <textarea className="form-textarea" rows={3} style={{ flex: 1, minWidth: 200 }}
              value={temp} onChange={e => setTemp(e.target.value)} />
          ) : (
            <input type={tipo} className="form-input" placeholder={placeholder} style={{ maxWidth: 260 }}
              value={temp} onChange={e => setTemp(e.target.value)} />
          )}
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditando(false); setTemp(valor || ''); }}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={guardar}>Guardar</button>
          </div>
        </div>
      ) : (
        <p style={{ fontSize: '.875rem', color: 'var(--gris-5)', whiteSpace: multilinea ? 'pre-wrap' : 'normal' }}>
          {valor || <span style={{ color: 'var(--gris-4)' }}>Sin datos</span>}
        </p>
      )}
    </div>
  );
}

function FechaEditable({ valor, onGuardar }) {
  const [editando, setEditando] = useState(false);
  const [temp, setTemp] = useState(valor || '');

  useEffect(() => setTemp(valor || ''), [valor]);

  const guardar = async () => {
    await onGuardar(temp);
    setEditando(false);
  };

  return editando ? (
    <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
      <input type="date" className="form-input" style={{ maxWidth: 200 }}
        value={temp} onChange={e => setTemp(e.target.value)} />
      <button className="btn btn-secondary btn-sm" onClick={() => { setEditando(false); setTemp(valor || ''); }}>Cancelar</button>
      <button className="btn btn-primary btn-sm" onClick={guardar}>Guardar</button>
    </div>
  ) : (
    <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
      <p style={{ fontSize: '.875rem', color: 'var(--gris-5)' }}>
        {valor || <span style={{ color: 'var(--gris-4)' }}>Sin fecha registrada</span>}
      </p>
      <button className="btn btn-outline btn-sm" onClick={() => setEditando(true)}>✏️ Editar</button>
    </div>
  );
}

function DetalleCliente({ id, onVolver }) {
  const navigate = useNavigate();
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);

  const cargar = async () => {
    const res = await api(`/clientes/${id}`);
    setDetalle(res);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, [id]);

  const guardarCampo = async (campo, valor) => {
    await api(`/clientes/${id}`, { method: 'PATCH', body: JSON.stringify({ [campo]: valor }) });
    cargar();
  };

  const guardarCampoVehiculo = async (idVehiculo, campo, valor) => {
    await api(`/vehiculos/${idVehiculo}`, { method: 'PATCH', body: JSON.stringify({ [campo]: valor }) });
    cargar();
  };

  const descartarPendiente = async (idPaquete) => {
    await api(`/clientes/presupuesto-paquete/${idPaquete}/descartar`, { method: 'PATCH' });
    cargar();
  };

  const marcarVendido = async (idVehiculo) => guardarCampoVehiculo(idVehiculo, 'estado_venta', 'vendido');
  const marcarActivo = async (idVehiculo) => guardarCampoVehiculo(idVehiculo, 'estado_venta', 'activo');
  const marcarComprado = async (idVehiculo) => guardarCampoVehiculo(idVehiculo, 'estado_venta', 'activo');
  const descartarEvaluacion = async (idVehiculo) => {
    await api(`/vehiculos/${idVehiculo}`, { method: 'DELETE' });
    cargar();
  };

  const eliminarCliente = async () => {
    await api(`/clientes/${id}`, { method: 'DELETE' });
    onVolver();
  };

  if (cargando) return <div className="page-content"><Spinner texto="Cargando cliente..." /></div>;
  if (!detalle) return null;

  return (
    <div className="page-content">
      <div className="sec-header">
        <button className="btn btn-secondary btn-sm" onClick={onVolver}>← Volver</button>
        <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }}
          onClick={() => setConfirmandoEliminar(true)}>🗑️ Eliminar cliente</button>
      </div>

      {confirmandoEliminar && (
        <ConfirmModal
          mensaje="¿Eliminar este cliente? Quedará oculto de las listas, pero se puede recuperar si hace falta."
          onConfirm={eliminarCliente}
          onCancel={() => setConfirmandoEliminar(false)}
        />
      )}

      {/* 1. Datos del cliente */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="sec-header" style={{ marginBottom: '.75rem' }}>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 600 }}>{detalle.nombre}</h2>
            <span className="badge badge-gris" style={{ marginTop: '.25rem' }}>{detalle.id}</span>
          </div>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button className="btn btn-primary btn-sm"
              onClick={() => navigate('/', { state: { clientePreseleccionado: detalle, vehiculoPreseleccionado: detalle.vehiculos[0] } })}>
              + Nueva orden
            </button>
            <button className="btn btn-outline btn-sm"
              onClick={() => navigate('/presupuestos', { state: { clientePreseleccionado: detalle, vehiculoPreseleccionado: detalle.vehiculos[0], crear: true } })}>
              + Nuevo presupuesto
            </button>
          </div>
        </div>

        {detalle.resumen_automatico && (
          <div style={{ marginBottom: '.75rem', padding: '.6rem .9rem', background: 'var(--azul-claro)',
                        borderRadius: 'var(--radio)', fontSize: '.875rem', color: 'var(--gris-5)' }}>
            <strong>Resumen:</strong> {detalle.resumen_automatico}
          </div>
        )}

        <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
          <CampoEditable etiqueta="Nombre completo" valor={detalle.nombre}
            onGuardar={(v) => guardarCampo('nombre', v)} placeholder="Nombre completo" />
          <CampoEditable etiqueta="Celular" valor={detalle.celular}
            onGuardar={(v) => guardarCampo('celular', v)} placeholder="Ej: 099 123 456" />
        </div>
        <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
          <CampoEditable etiqueta="RUT o CI" valor={detalle.rut}
            onGuardar={(v) => guardarCampo('rut', v)} placeholder="RUT (empresa) o CI (persona)" />
          <CampoEditable etiqueta="Dirección" valor={detalle.direccion}
            onGuardar={(v) => guardarCampo('direccion', v)} placeholder="Dirección" />
        </div>

        <div style={{ marginBottom: '.75rem' }}>
          <div className="sec-header" style={{ marginBottom: '.3rem' }}>
            <p style={{ fontWeight: 600, fontSize: '.8125rem', color: 'var(--gris-5)' }}>Fecha de ingreso / agendado</p>
          </div>
          <FechaEditable valor={detalle.fecha_ingreso} onGuardar={(v) => guardarCampo('fecha_ingreso', v)} />
        </div>

        <CampoEditable etiqueta="Notas originales" valor={detalle.notas_cliente} multilinea
          onGuardar={(v) => guardarCampo('notas_cliente', v)} />

        <div style={{ marginTop: '.75rem' }}>
          <CampoEditable etiqueta="Notas libres (editable)" valor={detalle.notas_libres} multilinea
            onGuardar={(v) => guardarCampo('notas_libres', v)} />
        </div>
      </div>

      {/* 2. Vehículos */}
      {detalle.vehiculos.map(v => (
        <div key={v.id} className="card" style={{
          marginBottom: '.75rem',
          borderLeft: `3px solid ${v.estado_venta === 'vendido' ? 'var(--gris-3)' : v.estado_venta === 'en_evaluacion' ? 'var(--naranja, #e8a33d)' : 'var(--azul-medio)'}`,
          opacity: v.estado_venta === 'vendido' ? 0.7 : 1
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <p style={{ fontWeight: 600, fontSize: '1rem' }}>
              🚗 {v.marca} {v.modelo} {v.anio ? `(${v.anio})` : ''}
              {v.estado_venta === 'vendido' && <span className="badge badge-gris" style={{ marginLeft: '.5rem' }}>Vendido</span>}
              {v.estado_venta === 'en_evaluacion' && <span className="badge badge-gris" style={{ marginLeft: '.5rem' }}>Vehículo en evaluación</span>}
            </p>
            {v.estado_venta === 'en_evaluacion' ? (
              <div style={{ display: 'flex', gap: '.4rem' }}>
                <button className="btn btn-success btn-sm" onClick={() => marcarComprado(v.id)}>🚗 Marcar comprado</button>
                <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }} onClick={() => descartarEvaluacion(v.id)}>Descartar</button>
              </div>
            ) : v.estado_venta === 'vendido' ? (
              <button className="btn btn-outline btn-sm" onClick={() => marcarActivo(v.id)}>Marcar activo</button>
            ) : (
              <button className="btn btn-outline btn-sm" onClick={() => marcarVendido(v.id)}>Marcar vendido</button>
            )}
          </div>

          <div className="resumen-grid" style={{ marginTop: '.6rem' }}>
            <CampoEditable etiqueta="Marca" valor={v.marca} onGuardar={(val) => guardarCampoVehiculo(v.id, 'marca', val)} />
            <CampoEditable etiqueta="Modelo" valor={v.modelo} onGuardar={(val) => guardarCampoVehiculo(v.id, 'modelo', val)} />
            <CampoEditable etiqueta="Año" valor={v.anio} onGuardar={(val) => guardarCampoVehiculo(v.id, 'anio', val)} />
            <CampoEditable etiqueta="Matrícula" valor={v.matricula} onGuardar={(val) => guardarCampoVehiculo(v.id, 'matricula', val)} />
            <CampoEditable etiqueta="Motor" valor={v.motor} onGuardar={(val) => guardarCampoVehiculo(v.id, 'motor', val)} />
            <CampoEditable etiqueta="Combustible" valor={v.combustible} onGuardar={(val) => guardarCampoVehiculo(v.id, 'combustible', val)} />
            <CampoEditable etiqueta="Transmisión" valor={v.transmision} onGuardar={(val) => guardarCampoVehiculo(v.id, 'transmision', val)} />
            <CampoEditable etiqueta="Admisión" valor={v.admision} onGuardar={(val) => guardarCampoVehiculo(v.id, 'admision', val)} />
            <CampoEditable etiqueta="Color" valor={v.color} onGuardar={(val) => guardarCampoVehiculo(v.id, 'color', val)} />
            <CampoEditable etiqueta="N° Chasis" valor={v.n_chasis} onGuardar={(val) => guardarCampoVehiculo(v.id, 'n_chasis', val)} />
            <CampoEditable etiqueta="N° Motor" valor={v.n_motor} onGuardar={(val) => guardarCampoVehiculo(v.id, 'n_motor', val)} />
          </div>

          <div className="form-grid-2" style={{ marginTop: '.75rem' }}>
            <CampoEditable etiqueta="🛢️ Último aceite usado" valor={v.ultimo_aceite}
              placeholder="Ej: 5W30 sintético"
              onGuardar={(val) => guardarCampoVehiculo(v.id, 'ultimo_aceite', val)} />
            <CampoEditable etiqueta="📍 Último km en taller" valor={v.ultimo_km}
              placeholder="Ej: 125000"
              onGuardar={(val) => guardarCampoVehiculo(v.id, 'ultimo_km', val)} />
          </div>

          {v.notas_vehiculo && (
            <p style={{ marginTop: '.6rem', fontSize: '.875rem', color: 'var(--gris-5)' }}>
              <strong>Notas:</strong> {v.notas_vehiculo}
            </p>
          )}
        </div>
      ))}

      {/* 3. Pendientes de presupuestos */}
      {detalle.pendientes && detalle.pendientes.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '3px solid #b45309' }}>
          <p style={{ fontWeight: 600, color: '#b45309', marginBottom: '.6rem' }}>⚠️ Pendientes de presupuestos</p>
          {detalle.pendientes.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '.4rem 0', borderBottom: '1px solid var(--gris-2)' }}>
              <div>
                <span style={{ fontWeight: 500 }}>{p.nombre}</span>
                <span style={{ color: 'var(--gris-4)', fontSize: '.8125rem', marginLeft: '.5rem' }}>
                  ${p.precio.toLocaleString()} — {p.id_presupuesto}
                </span>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => descartarPendiente(p.id_paquete)}>
                Ya no aplica
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 4. Órdenes de trabajo */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.6rem' }}>Órdenes de trabajo</p>
        {detalle.ordenes.length === 0 ? <Empty icon="📋" texto="Sin órdenes" /> :
          <div className="tabla-wrap">
            <table className="tabla">
              <thead><tr><th>ID</th><th>Fecha</th><th>Resumen</th><th>Total</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {detalle.ordenes.map(o => (
                  <tr key={o.id}>
                    <td><span className="badge badge-azul">{o.id}</span></td>
                    <td>{fmtFecha(o.fecha)}</td>
                    <td>{o.resumen || '—'}</td>
                    <td style={{ fontWeight: 500 }}>{o.total ? `$${o.total.toLocaleString()}` : '—'}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => navigate('/ordenes', { state: { idAbrir: o.id, idClienteOrigen: detalle.id } })}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {/* 5. Presupuestos */}
      <div className="card">
        <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.6rem' }}>Presupuestos</p>
        {detalle.presupuestos.length === 0 ? <Empty icon="📄" texto="Sin presupuestos" /> :
          <div className="tabla-wrap">
            <table className="tabla">
              <thead><tr><th>ID</th><th>Fecha</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {detalle.presupuestos.map(p => (
                  <tr key={p.id}>
                    <td><span className="badge badge-gris">{p.id}</span></td>
                    <td>{fmtFecha(p.fecha)}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => navigate('/presupuestos', { state: { idAbrir: p.id, idClienteOrigen: detalle.id } })}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
}
